import React, { useEffect } from "react";
import "../../App.css";
import LinkDrop from "./link_drop";
import SudokuIcon from "../Assets/sudoku.png";
import CombinationIcon from "../Assets/combination.png";
import NonoIcon from "../Assets/nonogram.png";
import SolitareIcon from "../Assets/solitare.png";
import SudoblockIcon from "../Assets/sudoblock.png";
import SudotetroidIcon from "../Assets/sudotetroid.png";
import { House, User, Settings } from "lucide-react";
import WordleIcon from "../Assets/wordle_logo.png";
import ConnectionsIcon from "../Assets/connections_logo.png";
import CrosswordIcon from "../Assets/crossword_logo.png";
import MinequestIcon from "../Assets/minequest_logo.png";
import SolitaireIcon from "../Assets/solitaire_logo.png";
import WordSearchIcon from "../Assets/word_search_logo.png";

import { useMediaQuery } from "@mui/material";
import BottomNavBar from "../../pages/MobileView/BottomNavBar";
import { post } from "../../lib/api";
import { get } from "../../lib/api";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, SidebarFooter, useSidebar } from "../ui/sidebar";
import ArchivedLinkDrop from "./link_drop_archive";

const pageIcons = {
  Settings: <Settings />,
  User: <User />,
  Combinations: CombinationIcon,
  Nonogram: NonoIcon,
  Solitare: SolitareIcon,
  Sudoku: SudokuIcon,
  Sudoblock: SudoblockIcon,
  Sudotetroid: SudotetroidIcon,
  Wordle: WordleIcon,
  Connections: ConnectionsIcon,
  Crossword: CrosswordIcon,
  Minequest: MinequestIcon,
  Solitaire: SolitaireIcon,
  "Word Search": WordSearchIcon,
};

const archivedGames = ["10 Daagse", "10 Daagse HLN"];

const Sidebarnav = () => {
  // Destructure values and setters from SidebarContext
  const { setBrand, setAccess, setDates, setAuthenticated, authenticated, structuredPages, setComponents, setPages, setUser, setUserChildren, setGames, pages, components, setStructuredPages, user } =
    useSidebar();

  // Get the current date formatted as YYYY-MM-DD
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // padStart ensures the month is always 2 digits
  const day = String(currentDate.getDate()).padStart(2, "0"); // padStart ensures the day is always 2 digits
  const formattedDate = `${year}-${month}-${day}`;
  const isMobile = useMediaQuery(`(max-width:768px)`);

  // Check user authentication status on component mount
  useEffect(() => {
    const fetchAuthStatus = async () => {
      try {
        const data = await get("/session");

        if (data["isValid"]) {
          setAuthenticated(data["isValid"]);
          setUser(data["user"]);
        }
      } catch (error) {
        console.error("Error:", error);
        setAuthenticated(false);
      }
    };

    fetchAuthStatus();
  }, [setAuthenticated, setUser]);

  // Fetch user-specific data once authenticated
  useEffect(() => {
    if (!authenticated || !user) return; // Exit if not authenticated or user is array or null

    const fetchData = async () => {
      try {
        const getData = {
          user: user,
          method: "getUserPageAccess",
        };
        const data = await post(getData);

        setBrand(data["brand"] || []);
        setAccess(data["access"] || []);
        setDates([data["start_date"], formattedDate]);
        setComponents(data["components"] || []);
        setPages(data["pages"] || []);
        setUser(data["user_info"] || null);
        setUserChildren(data["children"] || []);
        setGames(data["games"] || []);
        //console.log("raw login data: ", data);
      } catch (error) {
        console.error("Error:", error);
      }
    };

    fetchData();
  }, [authenticated]);

  // Build structuredPages object based on pages and components
  useEffect(() => {
    let structuredPages = {};
    if (Array.isArray(pages)) {
      pages.forEach((page) => {
        // Check if it's a subpage
        if (page && page.parent_id) {
          // Ensure the parent page exists in the structure
          if (!structuredPages[page.parent_id]) {
            structuredPages[page.parent_id] = { subpages: {} };
          } else {
            structuredPages[page.parent_id].subpages = structuredPages[page.parent_id].subpages || {};
          }
          // Add subpage to the parent page's subpages
          if (!structuredPages[page.parent_id].subpages[page.page_id]) {
            structuredPages[page.parent_id].subpages[page.page_id] = {};
          }
        } else if (page && page.page_id) {
          // If it's not a subpage, process it as a main page
          if (!structuredPages[page.page_id]) {
            structuredPages[page.page_id] = {};
          }
        }
      });
    }

    // Process components and assign them to their respective pages or subpages
    if (Array.isArray(components)) {
      components.forEach((component) => {
        if (component) {
          let targetPage = structuredPages[component.page_id];
          let targetSubpage;
          if (structuredPages[component.parent_id] && structuredPages[component.parent_id].subpages) {
            targetSubpage = structuredPages[component.parent_id].subpages[component.page_id];
          }

          if (targetPage) {
            // Add component to the main page
            targetPage.containers = targetPage.containers || [];
            targetPage.containers.push({
              title: component.component_id,
              tier: component.access_tier,
            });
          } else if (targetSubpage) {
            // Add component to the subpage
            targetSubpage.containers = targetSubpage.containers || [];
            targetSubpage.containers.push({
              title: component.component_id,
              tier: component.access_tier,
            });
          } else {
            console.error(`No target page or subpage found for component: ${component.component_id}`);
          }
        }
      });
    }

    // Update the structuredPages state
    setStructuredPages(structuredPages);
  }, [pages, components, setStructuredPages]);

  // Render sidebar only if user is authenticated
  return authenticated ? (
    isMobile ? (
      <BottomNavBar />
    ) : (
      <Sidebar
        collapsible="icon"
        className="!w-auto !h-[94vh] z-[10] flex justify-center items-start mt-16 space-y-5 fixed text-sidebar-primary-foreground
                     dark:text-[var(--text-light)]"
      >
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className={"!pl-0"}>
                <SidebarMenuItem className="min-w-full">
                  <SidebarMenuButton asChild className="min-w-full">
                    <a href={"/"} className={"no-underline appearance-none "}>
                      <House className={"size-6 stroke-black dark:stroke-white"} />
                      <span className="text-[var(--text-dark)] dark:text-[var(--text-light)] font-medium"> Home </span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarSeparator />
                {authenticated &&
                  Object.keys(structuredPages).map((pageKey) => {
                    if (pageKey.toLowerCase() === "administration") {
                      return null;
                    }
                    if (archivedGames.includes(pageKey)) {
                      return null;
                    }
                    const page = structuredPages[pageKey];
                    // const isActive = popout === pageKey;
                    return (
                      <LinkDrop
                        pageKey={pageKey}
                        Icon={pageIcons[pageKey]}
                        page={page}
                        currentPageKey={pageKey}
                        // isActive={isActive}
                      />
                    );
                  })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarSeparator />
          <SidebarMenu>
            <SidebarMenuItem>
              <ArchivedLinkDrop pages={Object.fromEntries(Object.entries(structuredPages).filter(([key]) => archivedGames.includes(key)))} />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    )
  ) : null;
};

export default Sidebarnav;
